// src/app/api/faithApi.ts
import { baseApi } from "./baseApi";

/**
 * Local types for Faith That Works leads.
 * If you already have shared types in your project (e.g. src/types/*),
 * feel free to move these there and replace the local types with imports.
 */
export type FaithLead = {
  _id?: string;
  firstName: string;
  lastName?: string;
  email: string;
  phoneNo?: string;
  source?: string;
  status?: "pre_registered" | "waiting" | "notified" | "enrolled";
  metrics?: {
    openedEmails?: number;
    clickedLinks?: number;
    sessionJoined?: number;
    lastActivityAt?: string | null;
  };
  consent?: {
    email?: boolean;
    sms?: boolean;
    consentGivenAt?: string | null;
  };
  meta?: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateFaithLeadRequest = Partial<
  Pick<
    FaithLead,
    "firstName" | "lastName" | "email" | "phoneNo" | "source" | "meta"
  >
>;

export type UpdateFaithLeadRequest = Partial<
  Pick<
    FaithLead,
    | "firstName"
    | "lastName"
    | "email"
    | "phoneNo"
    | "status"
    | "consent"
    | "meta"
  >
>;

/**
 * faithApi - RTK Query endpoints for Faith That Works lead CRUD
 */
export const faithApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create / Pre-register a lead
    createFaithLead: builder.mutation<FaithLead, CreateFaithLeadRequest>({
      query: (data) => ({
        url: "/faith-that-works",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "FaithLead", id: "LIST" }],
    }),

    // Get all leads
    getFaithLeads: builder.query<FaithLead[], void>({
      query: () => ({
        url: "/faith-that-works",
        method: "GET",
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: "FaithLead" as const, id: r._id })),
              { type: "FaithLead" as const, id: "LIST" },
            ]
          : [{ type: "FaithLead" as const, id: "LIST" }],
    }),

    // Get single lead by id
    getFaithLead: builder.query<FaithLead, string>({
      query: (id) => ({
        url: `/faith-that-works/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "FaithLead", id }],
    }),

    // Update a lead
    updateFaithLead: builder.mutation<
      FaithLead,
      { id: string; data: UpdateFaithLeadRequest }
    >({
      query: ({ id, data }) => ({
        url: `/faith-that-works/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FaithLead", id },
        { type: "FaithLead", id: "LIST" },
      ],
    }),

    // Delete a lead
    deleteFaithLead: builder.mutation<FaithLead, string>({
      query: (id) => ({
        url: `/faith-that-works/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "FaithLead", id },
        { type: "FaithLead", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

/**
 * Export hooks for usage in components
 */
export const {
  useCreateFaithLeadMutation,
  useGetFaithLeadsQuery,
  useGetFaithLeadQuery,
  useUpdateFaithLeadMutation,
  useDeleteFaithLeadMutation,
} = faithApi;
